import { Link } from 'react-router-dom';
import { ArrowRight, ShoppingCart, Search, Zap, MapPin, Shield, Sparkles, ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, useScroll, useTransform, useInView, Variants } from 'framer-motion';
import { useRef, useEffect } from 'react';
import Seo from '@/components/Seo';
import { Helmet } from 'react-helmet-async';

const features = [
  { icon: Search, title: 'Compare Prices', desc: 'Search across 6+ stores to find the lowest price for any grocery item.', color: 'from-primary/20 to-primary/5', iconColor: 'text-primary' },
  { icon: ShoppingCart, title: 'Smart Lists', desc: "Build a list and we'll show which store or combination saves you the most.", color: 'from-info/20 to-info/5', iconColor: 'text-info' },
  { icon: MapPin, title: 'Nearby Stores', desc: 'Auto-detect your location to show only stores that deliver to your area.', color: 'from-warning/20 to-warning/5', iconColor: 'text-warning' },
  { icon: Zap, title: 'Instant Savings', desc: 'Get a clear breakdown of exactly how much you save per trip.', color: 'from-destructive/20 to-destructive/5', iconColor: 'text-destructive' },
];

const storeLogos = [
  { name: 'Zepto', emoji: '⚡' },
  { name: 'Instamart', emoji: '🧡' },
  { name: 'Flipkart Minutes', emoji: '💛' },
  { name: 'BigBasket', emoji: '🟢' },
  { name: 'Blinkit', emoji: '🟡' },
  { name: 'JioMart', emoji: '🔵' },
];

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 60, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  },
};

function Section({ children, className = '', id }: { children: React.ReactNode; className?: string; id?: string }) {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { amount: 0.3 });
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  // subtle parallax: content moves slower than scroll
  const y = useTransform(scrollYProgress, [0, 1], ['8%', '-8%']);
  const opacity = useTransform(scrollYProgress, [0, 0.25, 0.75, 1], [0.2, 1, 1, 0.2]);
  const scale = useTransform(scrollYProgress, [0, 0.25, 0.75, 1], [0.94, 1, 1, 0.96]);

  return (
    <section
      ref={ref}
      id={id}
      className={`snap-start snap-always relative h-[100svh] w-full flex items-center overflow-hidden ${className}`}
    >
      <motion.div
        style={{ y, opacity, scale }}
        className="w-full"
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          {children}
        </motion.div>
      </motion.div>
    </section>
  );
}

export default function HomePage() {
  // Apply snap scrolling to the window (html element) only while HomePage is mounted
  useEffect(() => {
    const html = document.documentElement;
    html.classList.add('snap-y', 'snap-mandatory');
    // Avoid horizontal scrollbar from animated decorations
    const prevOverflowX = html.style.overflowX;
    html.style.overflowX = 'hidden';
    return () => {
      html.classList.remove('snap-y', 'snap-mandatory');
      html.style.overflowX = prevOverflowX;
    };
  }, []);

  const scrollToNext = () => {
    window.scrollBy({ top: window.innerHeight, behavior: 'smooth' });
  };

  return (
    <div className="-mt-16">
      <Seo
        title="Grozo — Compare grocery prices across Zepto, Blinkit & more"
        description="Compare real-time grocery prices across Zepto, Blinkit, BigBasket, Instamart, Flipkart Minutes & JioMart. Build smart lists and save ₹500+/month."
        path="/"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": "How Grozo helps you save on groceries",
          "step": [
            { "@type": "HowToStep", "name": "Compare Prices", "text": "Search across 6+ stores to find the lowest price for any grocery item." },
            { "@type": "HowToStep", "name": "Smart Lists", "text": "Build a list and we'll show which store or combination saves you the most." },
            { "@type": "HowToStep", "name": "Nearby Stores", "text": "Auto-detect your location to show only stores that deliver to your area." },
            { "@type": "HowToStep", "name": "Instant Savings", "text": "Get a clear breakdown of exactly how much you save per trip." }
          ]
        })}</script>
      </Helmet>
      {/* Hero */}
      <Section>
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <motion.div
            animate={{ scale: [1, 1.1, 1], rotate: [0, 5, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[700px] bg-[radial-gradient(ellipse_at_center,hsl(262_83%_58%/0.12)_0%,transparent_70%)]"
          />
          <motion.div
            animate={{ y: [0, -30, 0], x: [0, 20, 0] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-20 right-0 w-80 h-80 bg-[radial-gradient(circle,hsl(330_80%_55%/0.1)_0%,transparent_70%)]"
          />
          <motion.div
            animate={{ y: [0, 30, 0], x: [0, -20, 0] }}
            transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-40 left-0 w-72 h-72 bg-[radial-gradient(circle,hsl(210_80%_52%/0.08)_0%,transparent_70%)]"
          />
        </div>

        <div className="container text-center">
          <div className="mx-auto max-w-3xl">
            <motion.div variants={itemVariants} className="inline-flex items-center gap-2 rounded-full bg-primary/10 dark:bg-primary/15 border border-primary/20 px-5 py-2 text-xs font-bold uppercase tracking-wider text-primary mb-8">
              <Sparkles className="h-3.5 w-3.5" />
              Smart grocery savings
              <ChevronRight className="h-3 w-3" />
            </motion.div>

            <motion.h1 variants={itemVariants} className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black tracking-tight text-foreground leading-[1.05]">
              Stop overpaying
              <br />
              <span className="gradient-text">for groceries</span>
            </motion.h1>

            <motion.p variants={itemVariants} className="mt-7 text-lg md:text-xl text-muted-foreground max-w-xl mx-auto leading-relaxed font-medium">
              Compare prices across Zepto, Blinkit, BigBasket & more. Build smart lists that save you real money every week.
            </motion.p>

            <motion.div variants={itemVariants} className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="gap-2 text-base px-8 h-14 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 font-bold">
                <Link to="/compare">
                  Start Comparing
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="gap-2 text-base h-14 rounded-2xl font-bold border-2">
                <Link to="/list">
                  <ShoppingCart className="h-4 w-4" />
                  My List
                </Link>
              </Button>
            </motion.div>

            <motion.div variants={itemVariants} className="mt-12 flex items-center justify-center gap-6 text-xs font-semibold text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1.5"><Shield className="h-3.5 w-3.5 text-primary" /> Free to use</span>
              <span className="w-1.5 h-1.5 rounded-full bg-primary/30" />
              <span className="flex items-center gap-1.5"><Zap className="h-3.5 w-3.5 text-primary" /> Real-time prices</span>
              <span className="w-1.5 h-1.5 rounded-full bg-primary/30" />
              <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-primary" /> Save ₹500+/month</span>
            </motion.div>
          </div>
        </div>

        {/* Scroll cue */}
        <motion.button
          onClick={scrollToNext}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 8, 0] }}
          transition={{ opacity: { delay: 1.5, duration: 0.6 }, y: { duration: 1.8, repeat: Infinity, ease: 'easeInOut' } }}
          className="absolute bottom-24 md:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors z-10"
          aria-label="Scroll down"
        >
          <span className="text-[10px] font-bold uppercase tracking-widest">Scroll</span>
          <ChevronDown className="h-5 w-5" />
        </motion.button>
      </Section>

      {/* Features */}
      <Section>
        <div className="container">
          <motion.div variants={itemVariants} className="text-center mb-6 md:mb-14">
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-foreground">How it works</h2>
            <p className="mt-3 md:mt-4 text-muted-foreground max-w-md mx-auto text-sm md:text-lg font-medium">Four simple steps to start saving on your grocery bills</p>
          </motion.div>
          <div className="grid gap-3 md:gap-6 grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <motion.div
                key={f.title}
                variants={itemVariants}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                className="group rounded-2xl md:rounded-3xl border border-border/60 bg-card p-4 md:p-7 text-center shadow-sm transition-shadow duration-300 hover:shadow-xl"
              >
                <div className={`mx-auto mb-3 md:mb-5 flex h-12 w-12 md:h-16 md:w-16 items-center justify-center rounded-xl md:rounded-2xl bg-gradient-to-br ${f.color} ring-1 ring-border/30 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}>
                  <f.icon className={`h-5 w-5 md:h-7 md:w-7 ${f.iconColor}`} />
                </div>
                <h3 className="font-display text-base md:text-xl font-bold text-card-foreground">{f.title}</h3>
                <p className="mt-1.5 md:mt-2.5 text-xs md:text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* Store logos */}
      <Section>
        <div className="container">
          <motion.p variants={itemVariants} className="text-center text-sm font-bold uppercase tracking-wider text-muted-foreground mb-8">
            Comparing prices across top Indian stores
          </motion.p>
          <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-4">
            {storeLogos.map((s, i) => (
              <motion.div
                key={s.name}
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={{ once: false, amount: 0.5 }}
                transition={{ delay: i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ scale: 1.08, y: -4 }}
                className="flex items-center gap-2.5 rounded-2xl border border-border/60 bg-card px-6 py-3.5 text-sm font-semibold text-foreground shadow-sm"
              >
                <span className="text-xl">{s.emoji}</span>
                {s.name}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </Section>

      {/* CTA */}
      <Section>
        <div className="container">
          <motion.div
            variants={itemVariants}
            className="relative w-full overflow-hidden rounded-[2rem] p-10 md:p-16 text-center"
            style={{ background: 'var(--gradient-hero)' }}
          >
            <motion.div
              animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute top-0 right-0 w-72 h-72 rounded-full bg-white/10 blur-3xl -translate-y-1/2 translate-x-1/3"
            />
            <motion.div
              animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
              transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
              className="absolute bottom-0 left-0 w-56 h-56 rounded-full bg-white/10 blur-3xl translate-y-1/2 -translate-x-1/4"
            />

            <div className="relative z-10">
              <h2 className="font-display text-3xl md:text-5xl font-extrabold text-primary-foreground">
                Ready to save on groceries?
              </h2>
              <p className="mt-4 text-primary-foreground/80 max-w-md mx-auto text-lg font-medium">
                Start comparing prices now. It's free and takes just seconds.
              </p>
              <Button asChild size="lg" className="mt-10 gap-2 rounded-2xl bg-background text-foreground hover:bg-background/90 shadow-xl px-8 h-14 text-base font-bold">
                <Link to="/compare">
                  <Search className="h-4 w-4" />
                  Compare Now
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </Section>
    </div>
  );
}
